import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LockKeyhole, Mail } from 'lucide-react';
import adminLogo from '../assets/headerlogo.png';
import englishService from '../services/englishService';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (englishService.auth.isAuthenticated()) {
      navigate('/dashboard-selection');
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      return;
    }
    
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    setError('');
    setLoading(true);

    try {
      await englishService.auth.login({
        email: formData.email,
        password: formData.password
      });

      if (formData.rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      }

      navigate('/dashboard-selection');
    } catch (err) {
      const errorMessage = err.message.includes('429') 
        ? 'Too many login attempts. Please wait a moment and try again.'
        : err.message || 'Failed to login. Please check your credentials.';
      
      setError(errorMessage);
      
      await new Promise(resolve => setTimeout(resolve, 2000));
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="bg-gradient-to-b from-sky-500 via-white to-white rounded-2xl shadow-xl w-full max-w-md p-8 space-y-8">
        <div className="w-96 h-16 mx-auto rounded-lg overflow-hidden">
          <img
            src={adminLogo}
            alt="Admin Logo"
            className="w-full h-full object-contain"
          />
        </div>

        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">Welcome Back</h2>
          <p className="mt-2 text-black">Please sign in to your account</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium text-black block">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black" size={20} />
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-3 border border-sky-400 rounded-lg focus:ring-2 focus:ring-white focus:border-white outline-none transition-colors bg-white bg-opacity-90"
                placeholder="Enter your email"
                required
                disabled={loading || isSubmitting}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="text-sm font-medium text-black block">
              Password
            </label>
            <div className="relative">
              <LockKeyhole className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                className="w-full pl-10 pr-12 py-3 border border-sky-400 rounded-lg focus:ring-2 focus:ring-white focus:border-white outline-none transition-colors bg-white bg-opacity-90"
                placeholder="Enter your password"
                required
                disabled={loading || isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                disabled={loading || isSubmitting}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="flex items-center">
            <input
              id="rememberMe"
              name="rememberMe"
              type="checkbox"
              checked={formData.rememberMe}
              onChange={handleChange}
              className="h-4 w-4 text-sky-500 border-sky-400 rounded focus:ring-sky-500 bg-white"
              disabled={loading || isSubmitting}
            />
            <label htmlFor="rememberMe" className="ml-2 text-sm text-black">
              Remember me
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || isSubmitting}
            className={`w-full bg-white text-sky-500 py-3 px-4 rounded-lg border-black hover:bg-opacity-90 focus:ring-4 focus:ring-white focus:ring-opacity-50 font-medium transition-colors shadow-md ${
              (loading || isSubmitting) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {(loading || isSubmitting) ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;